"use client";

import React, { useState, useCallback, useEffect } from "react";
import { Ticket } from "@/lib/types/database";
import { useTicketEditMode } from "@/contexts/TicketEditModeContext";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { ClampedText } from "@/components/shared/ClampedText";
import { CollapsibleSection } from "@/components/shared/CollapsibleSection";

interface TicketNotesProps {
  ticket: Ticket;
}

function hasAnyNotes(ticket: Ticket): boolean {
  return !!(ticket.internal_notes || ticket.customer_visible_notes);
}

export function TicketNotes({ ticket }: TicketNotesProps) {
  const editMode = useTicketEditMode();

  const hasAny = hasAnyNotes(ticket);
  const showSection = hasAny || !!editMode?.isEditMode;

  // Not in edit mode: only show when there's at least one note (current behavior).
  // Notes are secondary free-text — collapse the group by default to reduce
  // information overload; the content is one click away.
  if (!editMode?.isEditMode) {
    if (!hasAny) return null;
    return (
      <CollapsibleSection title="Notes" defaultCollapsed>
        <NotesContent ticket={ticket} readOnly />
      </CollapsibleSection>
    );
  }

  // Edit mode: always show section (auto-expanded)
  return (
    <div className="space-y-4">
      <h4 className="text-md font-semibold text-card-foreground">Notes</h4>
      {showSection && (
        <NotesContent
          ticket={ticket}
          readOnly={false}
          onUpdate={editMode.onUpdate}
        />
      )}
    </div>
  );
}

function NotesContent({
  ticket,
  readOnly,
  onUpdate,
}: {
  ticket: Ticket;
  readOnly: boolean;
  onUpdate?: (updates: Record<string, unknown>) => void;
}) {
  if (readOnly) {
    return (
      <>
        {ticket.customer_visible_notes && (
          <div className="space-y-2">
            <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">Customer Notes</h5>
            <div className="bg-blue-50 rounded-lg p-3 dark:bg-blue-950/40">
              <ClampedText
                text={ticket.customer_visible_notes}
                lines={4}
                className="text-sm text-gray-700 dark:text-gray-300"
              />
            </div>
          </div>
        )}
        {ticket.internal_notes && (
          <div className="space-y-2">
            <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">Internal Notes</h5>
            <div className="bg-yellow-50 rounded-lg p-3 dark:bg-yellow-950/40">
              <ClampedText
                text={ticket.internal_notes}
                lines={4}
                className="text-sm text-gray-700 dark:text-gray-300"
              />
            </div>
          </div>
        )}
      </>
    );
  }

  // Editable: always show both note areas with textareas
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">Customer Notes</h5>
        <EditableNoteField
          value={ticket.customer_visible_notes ?? ""}
          field="customer_visible_notes"
          onSave={(value) => onUpdate?.({ customer_visible_notes: value || null })}
          placeholder="Notes visible to the customer..."
          className="bg-blue-50 border-blue-200 focus:border-blue-400 dark:bg-blue-950/40 dark:border-blue-800"
        />
      </div>
      <div className="space-y-2">
        <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">Internal Notes</h5>
        <EditableNoteField
          value={ticket.internal_notes ?? ""}
          field="internal_notes"
          onSave={(value) => onUpdate?.({ internal_notes: value || null })}
          placeholder="Internal-only notes (not visible to customer)..."
          className="bg-yellow-50 border-yellow-200 focus:border-yellow-400 dark:bg-yellow-950/40 dark:border-yellow-800"
        />
      </div>
    </div>
  );
}

function EditableNoteField({
  value,
  field,
  onSave,
  placeholder,
  className,
}: {
  value: string;
  field: string;
  onSave: (value: string) => void;
  placeholder: string;
  className?: string;
}) {
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const currentTrimmed = (value ?? "").trim();
  const hasDirty = localValue.trim() !== currentTrimmed;

  const handleConfirm = useCallback(() => {
    onSave(localValue.trim() || "");
  }, [localValue, onSave]);

  const handleCancel = useCallback(() => {
    setLocalValue(value ?? "");
  }, [value]);

  return (
    <div className="space-y-2">
      <Textarea
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        placeholder={placeholder}
        rows={4}
        className={className}
        data-field={field}
      />
      {hasDirty && (
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={handleConfirm}>
            Confirm
          </Button>
          <Button size="sm" variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}
